import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Navbar } from "../../../components/layout/Navbar";
import { Footer } from "../../../components/layout/Footer";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Mail, Lock, ArrowLeft } from "lucide-react";
import { useToast } from "../../../hooks/use-toast";
import { useAuth } from "../../../contexts/AuthContext";
import { useLanguage } from "../../../contexts/LanguageContext";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const { login, user, userType } = useAuth(); // Get user and userType from auth context
  const { toast } = useToast();
  const navigate = useNavigate();
  const { language } = useLanguage();

  useEffect(() => {
    if (user) {
      // Redirect based on user role if already logged in
      if (userType === 'admin') {
        navigate('/admin');
      } else if (userType === 'investor') {
        navigate('/dashboard/investor');
      } else if (userType === 'project-holder') {
        navigate('/dashboard/project-holder');
      } else {
        navigate('/');
      }
    }
  }, [user, userType, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Simple validation
    if (!formData.email || !formData.password) {
      toast({
        title: language === "ar" ? "خطأ" : "Error",
        description: language === "ar" ? "يرجى ملء جميع الحقول" : "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      // First, try admin login
      const isAdmin = await login(formData.email, formData.password, "admin");

      if (isAdmin) {
        toast({
          title: language === "ar" ? "تسجيل دخول ناجح" : "Login Successful",
          description: language === "ar" ? "مرحباً بك في لوحة تحكم الإدارة!" : "Welcome to the admin dashboard!",
        });
        navigate("/admin");
        return;
      }

      // Try investor login
      const isInvestor = await login(formData.email, formData.password, "investor");

      if (isInvestor) {
        toast({
          title: language === "ar" ? "تسجيل دخول ناجح" : "Login Successful",
          description: language === "ar" ? "مرحباً بك مجدداً!" : "Welcome back!",
        });

        // Redirect to investor dashboard
        navigate("/dashboard/investor");
        return;
      }

      // Try project holder login
      const isProjectHolder = await login(formData.email, formData.password, "project-holder");

      if (isProjectHolder) {
        toast({
          title: language === "ar" ? "تسجيل دخول ناجح" : "Login Successful",
          description: language === "ar" ? "مرحباً بك مجدداً!" : "Welcome back!",
        });

        // Redirect to project holder dashboard
        navigate("/dashboard/project-holder");
        return;
      }

      throw new Error(language === "ar" ? "بيانات الاعدار غير صحيحة" : "Invalid credentials");
    } catch (error: any) {
      toast({
        title: language === "ar" ? "فشل تسجيل الدخول" : "Login Failed",
        description: language === "ar" ? "بيانات الاعدار غير صحيحة" : "Invalid credentials",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" dir={language === "ar" ? "rtl" : "ltr"}>
      <Navbar />

      <main className="flex-1 py-12">
        <div className="container mx-auto px-4 max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {language === "ar" ? "العودة للرئيسية" : "Back to Home"}
          </Link>

          <Card className="border-border shadow-lg animate-fade-in">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">
                {language === "ar" ? "تسجيل الدخول" : "Login"}
              </CardTitle>
              <CardDescription>
                {language === "ar" ? "أدخل بياناتك للوصول إلى حسابك" : "Enter your credentials to access your account"}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{language === "ar" ? "البريد الإلكتروني" : "Email"}</Label>
                  <div className="relative">
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="example@email.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={language === "ar" ? "pr-10" : "pl-10"}
                      required
                    />
                    <Mail className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">{language === "ar" ? "كلمة المرور" : "Password"}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={language === "ar" ? "pr-10" : "pl-10"}
                      required
                    />
                    <Lock className={`absolute ${language === "ar" ? "right-3" : "left-3"} top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground`} />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={loading}
                >
                  {loading ?
                    (language === "ar" ? "جاري تسجيل الدخول..." : "Logging in...") :
                    (language === "ar" ? "تسجيل الدخول" : "Login")}
                </Button>

                <div className="text-center pt-4">
                  <p className="text-muted-foreground">
                    {language === "ar" ? "ليس لديك حساب؟" : "Don't have an account?"}{" "}
                    <Link
                      to="/register"
                      className="text-primary hover:underline"
                    >
                      {language === "ar" ? "إنشاء حساب جديد" : "Create new account"}
                    </Link>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Login;